/**
 * Page-local copy and ids for /services and /services/:slug.
 *
 * The services themselves are not here. They are in `content/services.ts`,
 * which is shared because the header menu and the footer read it too — a
 * catalogue that lived in this folder would be a catalogue the navigation had
 * to retype. What lives here is the material the *pages* needed and the
 * catalogue had no business holding: the ids the bands are labelled by, the
 * three words the intro strip has room for, the hub's own questions, the
 * comparison table's column headings, and the situations band.
 *
 * PENDING KABEX (§59), on the same terms as `content/services.ts`: the
 * service names are approved, everything written around them is ours.
 */

import { type Crumb, type FaqItem, type PageIntroHighlight } from '@/components/molecules';
import { BALANCE_DEADLINE, DEPOSIT_RANGE } from '@/content/paymentTerms';
import { serviceCatalogue, type ServiceSlug } from '@/content/services';
import { type IconKey } from '@/content/site';
import { spellCount, spellCountSentence } from '@/utils/numberWords';

/** `Breadcrumbs` renders Home itself; the schema has to be told about it. */
export const crumbs: readonly Crumb[] = [{ label: 'Services' }];

/** The trail a service page sits on. Its own name is appended by the page. */
export const serviceCrumbTrail: readonly Crumb[] = [{ label: 'Services', to: '/services' }];

export const CATALOGUE_TITLE_ID = 'catalogue-title';
export const COMPARE_TITLE_ID = 'compare-title';
export const SITUATIONS_TITLE_ID = 'situations-title';
export const FAQ_TITLE_ID = 'services-faq-title';
export const OVERVIEW_TITLE_ID = 'service-overview-title';
export const SCOPE_TITLE_ID = 'service-scope-title';
export const SIBLINGS_TITLE_ID = 'service-siblings-title';

/**
 * The hub's anchor, and the target of the intro's one action.
 *
 * Exported because two files need it and neither should retype it: the page
 * puts it on the band, and the intro's `action` points at `#${CATALOGUE_ID}`.
 * Anything a `#id` points at needs a `scrollMarginTop` or the sticky header
 * lands on top of it — see `PageIntroActions`.
 */
export const CATALOGUE_ID = 'catalogue';

/** The comparison band's anchor, for the catalogue's "compare them" link. */
export const COMPARE_ID = 'compare';

/**
 * How many services there are, spelled, for the copy that quotes the number.
 *
 * Counted rather than typed. Several strings across these pages quote the count,
 * and the day the catalogue changes every one of them becomes a lie that
 * nothing fails on — the kind of drift only ever found by a customer. That is
 * not hypothetical here: the count went from seven to six when the warehouse
 * listing was removed, and every derived string follows on its own. Spelled
 * rather than interpolated raw because these are
 * sentences, and the clauses around them spell "one".
 */
export const SERVICE_COUNT = serviceCatalogue.length;
export const serviceCountWord = spellCount(SERVICE_COUNT);
export const ServiceCountWord = spellCountSentence(SERVICE_COUNT);

export const WHATSAPP_MESSAGE =
  'Hello Kabex Motors, I would like to know more about your services.';

/**
 * Three proof points: the services (§7), the one team that runs them
 * (§9) and the office they are run from (§14). Each restates a fact a band
 * below goes on to state in full.
 */
export const introHighlights: readonly PageIntroHighlight[] = [
  { icon: 'team', label: `${ServiceCountWord} services, one team` },
  { icon: 'location', label: 'Destination agreed in advance' },
  { icon: 'quote', label: 'Quotations free, always' },
];

/**
 * The comparison table's headings.
 *
 * The table exists because of the one thing a grid of cards cannot do, and
 * the reason is in the research rather than in taste: a visitor reading six
 * descriptions in sequence holds none of them side by side, so the question
 * they actually came with — which of these do I need, and which of them cost
 * me anything — is answered six times and compared never. Four columns,
 * every row a link to the page behind it, because a comparison that does not
 * lead anywhere is a dead end at the exact moment the reader has decided.
 */
export const compareColumns = {
  service: 'Service',
  outcome: 'What you end up with',
  cost: 'Cost',
  stage: 'Stage',
} as const;

/**
 * One situation a visitor arrives in, and the service that answers it.
 *
 * This band is the reason the page is not just a prettier version of the home
 * strip. A catalogue answers "what do you sell", which is the company's
 * question; nobody arrives with it. They arrive with "my car is stuck at the
 * port" or "the duty is more than I budgeted", and a grid of nouns makes them
 * map their own problem onto our vocabulary before they can act.
 *
 * These are the situations people actually turn up in.
 */
export type ServiceSituation = {
  icon: IconKey;
  /** In the visitor's words, not ours. */
  situation: string;
  /** What we do about it, in one line. */
  answer: string;
  service: ServiceSlug;
};

export const serviceSituations: readonly ServiceSituation[] = [
  {
    icon: 'search',
    situation: 'I do not know which car to buy',
    answer: 'Tell us the budget and what the car is for, and we will shortlist against that.',
    service: 'import-consultation',
  },
  {
    icon: 'calculator',
    situation: 'I know the car. What will it really cost?',
    answer: 'A written quotation covering the car, the taxes, transport and plates. Free.',
    service: 'tax-inquiries-and-quotations',
  },
  {
    icon: 'car',
    situation: 'I want a particular car, not whatever is on the lot',
    answer: 'We source the exact make, model, year and trim, and import it in your name.',
    service: 'car-importation',
  },
  {
    icon: 'clearing',
    situation: 'My car has arrived and it is not moving',
    answer:
      'We take on clearing and transport as its own job, including imports we did not arrange.',
    service: 'clearing-forwarding-and-logistics',
  },
  {
    icon: 'finance',
    situation: 'The duty bill is more than I planned for',
    answer: 'We check the assessment first, then set out the options for meeting it.',
    service: 'tax-financing',
  },
  {
    icon: 'registration',
    situation: 'My car is cleared but has no plates',
    answer: 'First-time registration run through URA, UNBS and the Ministry of Works.',
    service: 'local-vehicle-registration',
  },
];

/**
 * The questions the hub raises, answered on it.
 *
 * Deliberately about *the catalogue* rather than about importing. The import
 * questions are answered on /import-a-car and across /import-guide, and the
 * service pages each answer their own; what no other page answers is whether
 * these have to be bought together, what they cost, and whether Kabex will
 * take on a job somebody else started. Those are the questions a services
 * page exists to answer.
 *
 * Fed to `faqSchema` from this same array, so the page and its structured
 * data cannot drift (see `utils/structuredData.ts`).
 */
export const serviceFaq: readonly FaqItem[] = [
  {
    question: `Do I have to use all ${serviceCountWord} services?`,
    answer:
      'No. Most customers use three or four — a quotation, the import, clearing and registration — and plenty use one. The services are listed separately because they are sold separately; the reason they are listed together is that when you do need several, one team runs them.',
  },
  {
    question: 'Can you take over an import somebody else started?',
    answer:
      'Yes. Clearing and registration may be available as standalone services. Bring the documents and we will confirm what Kabex can handle and quote before work starts.',
  },
  {
    question: 'Which of these are free?',
    answer:
      'Tax inquiries, quotations and import consultation are free, and they are not conditional on buying through us. That is deliberate: a buyer who understands the full cost before committing is a better customer than one who finds out at the port.',
  },
  {
    question: 'What does it cost to start?',
    answer: `After you review the quotation and give a clear go-ahead, the import starts at ${DEPOSIT_RANGE} of the CIF price — your quotation says which — with the balance due ${BALANCE_DEADLINE} and payable in instalments. Everything else is quoted per job, priced before the work starts.`,
  },
  {
    question: 'Do you serve customers outside Uganda?',
    answer:
      'Yes. CIF destinations may include Mombasa, Kabex’s Kampala office, Bunagana or Nimule. Your quotation confirms the agreed destination and whether clearing or inland transport is included in your package.',
  },
  {
    question: 'Does Kabex have a vehicle warehouse?',
    answer:
      'No. Kabex does not own or operate a vehicle warehouse. Your car is handed over at the CIF destination agreed in your order. Any clearing or onward transport Kabex handles depends on your package and is stated in your quotation.',
  },
  {
    question: 'Can I pay for any of this through the website?',
    answer:
      'No, and we will never ask you to. Nothing on this site collects money. Every payment is against an invoice, into the Kabex Motors company account — never a personal one. If a request looks different, call the office number on this page before you send anything.',
  },
];
